import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './OtherProjectsPage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import { isContentfulConfigured } from '../contentful/client';
import { fetchOtherProjectsPageData } from '../contentful/otherProjectsApi';

const FallbackProjects = [
  {
    id: 'sintel',
    title: 'Sintel',
    description:
      'Sintel is an ecosystem for time series tasks including, time series featurization, anomaly detection, forecasting, and predictive maintenance. Sintel provides the ability to customize tools for particular domains such as predictive maintenance for wind energy industry.',
  },
  {
    id: 'sibyl',
    title: 'Sibyl',
    description:
      'Sibyl is a highly-configurable system for developing usable ML applications for any domain. Sibyl creates explanations of ML models that are rapidly understandable to audiences of all kinds, including those without ML experience.',
  },
  {
    id: 'deep-mining',
    title: 'Deep Mining',
    description:
      'A tool to automatically tune the entire data processing pipeline by standardizing the pipeline abstractions, as well as building and testing several hyperparameter selection and optimization methods. Saves a significant amount of time and effort, as small differences in hyperparameters can significantly affect the performance of a particular pipeline.',
  },
  {
    id: 'feature-hub',
    title: 'Feature Hub',
    description:
      'A tool that makes feature engineering easier, faster, and more effective by collaboratively crowdsourcing feature creation via an open-source web notebook. FeatureHub gathers, tests, collates and extracts features submitted by users, and uses them to train relevant machine learning models.',
  },
  {
    id: 'atm',
    title: 'Auto Tune Models (ATM)',
    description:
      'A cloud-based modeling system which provides an easier, faster way to narrow down to the best possible model for a given prediction problem. ATM performs bandit-based and Gaussian process learning to decide among methodologies, as well as to pinpoint which parameters and hyperparameters should be used for modeling.',
  },
  {
    id: 'sense-ml',
    title: 'Sense ML',
    description:
      'A cloud-based master-worker architecture that can process IOT data from segmentation all the way through the formation of a machine learning example. In the SenseML platform, some steps—such as pre-processing—are automated, while others are surfaced strategically to allow data scientists to input their expertise.',
  },
  {
    id: 'trane',
    title: 'Trane',
    description:
      'A language for describing prediction problems over relational datasets, allowing data scientists to specify problems in that language. TRANE automatically generates prediction problems for temporal datasets and produces labels for supervised learning. Its goal is to streamline the machine learning problem-solving process.',
  },
  {
    id: 'adel',
    title: 'Automatic Data Elements Linking (ADEL)',
    description:
      'A tool that generates a relational data schema and identifies other salient properties for a data model without meta information. It detects the type of each data field, possible Primary Keys for each row, and relationships between different data entities.',
  },
  {
    id: 'sdv',
    title: 'Synthetic Data Vault (SDV)',
    description:
      'SDV enables data scientists to sidestep data-sharing concerns and expand the pool of possible problem participants by generating synthetic data. By learning a generative model that accounts for dependence and relationships, the SDV creates new data that resembles the original set statistically, formally, and structurally.',
  },
];

const ImagePlaceholder = () => (
  <div className={styles.placeholder}>
    <svg
      viewBox="0 0 100 75"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.placeholderSvg}
    >
      <rect x="1" y="1" width="98" height="73" fill="none" stroke="#b0bec5" strokeWidth="1.5" />
      <line x1="1" y1="1" x2="99" y2="74" stroke="#b0bec5" strokeWidth="1.5" />
      <line x1="99" y1="1" x2="1" y2="74" stroke="#b0bec5" strokeWidth="1.5" />
    </svg>
  </div>
);

function ProjectLogo({ logoUrl, title }) {
  const [loadFailed, setLoadFailed] = useState(false);

  if (!logoUrl || loadFailed) {
    return <ImagePlaceholder />;
  }
  return (
    <img
      src={logoUrl}
      alt={`${title} logo`}
      className={styles.logo}
      onError={() => setLoadFailed(true)}
    />
  );
}

const OtherProjectsPage = () => {
  usePageMeta(pagesSeo.projects);
  const [projects, setProjects] = useState(FallbackProjects);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!isContentfulConfigured()) {
        return;
      }

      setStatus('loading');
      try {
        const data = await fetchOtherProjectsPageData();
        if (!mounted) return;
        setProjects(data);
        setStatus('success');
      } catch (err) {
        console.error('Failed to load Other Projects from Contentful', err);
        if (!mounted) return;
        setStatus('error');
        setProjects(FallbackProjects);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.container}>
        <h1 className={`${styles.title} page-primary-title`} style={{ ...typography.heading.large, color: colors.dark }}>
          OTHER PROJECTS
        </h1>

        <div className={styles.divider} />

        {status === 'loading' && (
          <p style={{ ...typography.body.medium.regular, color: colors.gray[700], marginBottom: 16 }}>
            Loading latest projects…
          </p>
        )}

        <div className={styles.grid}>
          {projects.map((project) => (
            <div key={project.id || project.title} className={styles.card}>
              <ProjectLogo logoUrl={project.logoUrl} title={project.title} />
              <h2 className={styles.projectName}>{project.title}</h2>
              <p className={styles.description}>{project.description}</p>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OtherProjectsPage;
